import React, { useState, useEffect } from 'react';
import { apiService } from '../services/api';
import { authService } from '../services/auth';
import { analyticsService } from '../services/analytics';
import { Dialog } from './radix/Dialog';
import { Label } from './radix/Label';
import { CATEGORY_NAMES, getCategoryIcon } from '../constants/categories';
import './CreatePollForm.css';

import { Poll } from '../types';

interface CreatePollFormProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onPollCreated: (poll: Poll) => void;
}

export const CreatePollForm: React.FC<CreatePollFormProps> = ({
  open,
  onOpenChange,
  onPollCreated,
}) => {
  const [question, setQuestion] = useState('');
  const [options, setOptions] = useState(['', '']);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Category state
  const [suggestedCategory, setSuggestedCategory] = useState<string>('General');
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [showOverride, setShowOverride] = useState(false);

  // Debounced AI categorization
  useEffect(() => {
    // Only trigger if we have a question and at least 2 filled options
    const validOptions = options.filter((opt) => opt.trim());
    if (!question.trim() || validOptions.length < 2) {
      return;
    }

    // Debounce by 500ms
    const timer = setTimeout(async () => {
      setIsAnalyzing(true);
      try {
        const response = await apiService.categorizePoll({
          question: question.trim(),
          options: validOptions,
        });
        setSuggestedCategory(response.category);
      } catch (error) {
        console.error('Failed to categorize poll:', error);
        setSuggestedCategory('General'); // Fallback
      } finally {
        setIsAnalyzing(false);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [question, options]);

  // Calculate dynamic font size based on question length and screen width
  const getQuestionFontSize = () => {
    const length = question.length;
    const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;

    if (isMobile) {
      if (length < 50) return '20px';
      if (length < 100) return '18px';
      return '16px'; // Prevent iOS auto-zoom
    }

    if (length === 0) return '24px';
    if (length < 50) return '24px';
    if (length < 100) return '20px';
    if (length < 200) return '18px';
    if (length < 300) return '16px';
    return '16px'; // Prevent iOS auto-zoom
  };

  const handleOptionChange = (index: number, value: string) => {
    const newOptions = [...options];
    newOptions[index] = value;
    setOptions(newOptions);
  };

  const addOption = () => {
    if (options.length < 10) {
      setOptions([...options, '']);
    }
  };

  const removeOption = (index: number) => {
    if (options.length > 2) {
      setOptions(options.filter((_, i) => i !== index));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Validate
    if (!question.trim()) {
      setError('Question is required');
      return;
    }

    const validOptions = options.filter((opt) => opt.trim());
    if (validOptions.length < 2) {
      setError('At least 2 options are required');
      return;
    }

    setIsSubmitting(true);

    try {
      // Use selected category if user overrode, otherwise use AI suggestion
      const category = selectedCategory || suggestedCategory;

      const createdPoll = await apiService.createPoll({
        question: question.trim(),
        options: validOptions,
        category,
      });

      // Track poll creation in analytics
      try {
        const userInfo = authService.getUserInfo();
        analyticsService.trackPollCreated(
          createdPoll.pollId,
          createdPoll.category || "General",
          userInfo?.sub || "anonymous_user"
        );
      } catch (analyticsErr) {
        console.warn("Failed to track poll creation in analytics:", analyticsErr);
      }

      // Reset form and notify parent with the created poll
      setQuestion('');
      setOptions(['', '']);
      setSuggestedCategory('General');
      setSelectedCategory(null);
      setShowOverride(false);
      onPollCreated(createdPoll);
    } catch (err: any) {
      setError(err.message || 'Failed to create poll');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title="New poll"
      className="create-poll-form"
      onOpenAutoFocus={(e) => {
        if (typeof window !== 'undefined' && window.innerWidth < 768) {
          e.preventDefault();
        }
      }}
    >
      <div className="create-poll-form-content">
        {error && <div className="error-message">{error}</div>}

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <Label htmlFor="question">Question</Label>
            <textarea
              id="question"
              className="question-textarea"
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              placeholder="What's your question?"
              maxLength={500}
              disabled={isSubmitting}
              style={{ fontSize: getQuestionFontSize() }}
              rows={4}
            />
          </div>

          <div className="form-group">
            <Label>Options</Label>
            {options.map((option, index) => (
              <div key={index} className="option-input">
                <input
                  type="text"
                  value={option}
                  onChange={(e) => handleOptionChange(index, e.target.value)}
                  placeholder={`Option ${index + 1}`}
                  maxLength={200}
                  disabled={isSubmitting}
                />
                {options.length > 2 && (
                  <button
                    type="button"
                    onClick={() => removeOption(index)}
                    className="remove-option-btn"
                    disabled={isSubmitting}
                  >
                    ✕
                  </button>
                )}
              </div>
            ))}

            {options.length < 10 && (
              <button
                type="button"
                onClick={addOption}
                className="add-option-btn"
                disabled={isSubmitting}
              >
                + Add Option
              </button>
            )}
          </div>

          {/* AI Category Suggestion */}
          {question.trim() && options.filter((opt) => opt.trim()).length >= 2 && (
            <div className="category-section">
              {isAnalyzing ? (
                <div className="category-suggestion analyzing">
                  ✨ Analyzing... ⏳
                </div>
              ) : showOverride ? (
                <div className="category-override">
                  <Label htmlFor="category-select">Category</Label>
                  <select
                    id="category-select"
                    value={selectedCategory || suggestedCategory}
                    onChange={(e) => setSelectedCategory(e.target.value)}
                    disabled={isSubmitting}
                    className="category-select"
                  >
                    {CATEGORY_NAMES.map((categoryName) => (
                      <option key={categoryName} value={categoryName}>
                        {getCategoryIcon(categoryName)} {categoryName}
                      </option>
                    ))}
                  </select>
                  <button
                    type="button"
                    onClick={() => setShowOverride(false)}
                    className="category-link"
                    disabled={isSubmitting}
                  >
                    Use AI suggestion
                  </button>
                </div>
              ) : (
                <div className="category-suggestion">
                  <div className="category-suggestion-text">
                    ✨ Suggested:{' '}
                    <span className="category-chip">
                      {getCategoryIcon(suggestedCategory)} {suggestedCategory}
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={() => setShowOverride(true)}
                    className="category-link"
                    disabled={isSubmitting}
                  >
                    Change
                  </button>
                </div>
              )}
            </div>
          )}

          <div className="form-actions">
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              className="cancel-btn"
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button type="submit" className="submit-btn" disabled={isSubmitting}>
              {isSubmitting ? 'Creating...' : 'Create Poll'}
            </button>
          </div>
        </form>
      </div>
    </Dialog>
  );
};
